import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';
import _ from 'underscore';

import {FeatureAttributes, FeatureLibraryAttributes} from 'molbio/feature-libraries/FeatureLibrary';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import ScrollableTable from 'ui-library/scrollable/ScrollableTable';
import {RowSelectionMode, SortDirection} from 'ui-library/scrollable/ScrollableTableConstants';
import {cycleSortDirection} from 'ui-library/scrollable/ScrollableTableUtil';
import sortedBy from 'util/sortedBy';

export interface FeatureTableProps {
  features: Array<FeatureAttributes>;
  onUpdateFeature: (
    featureIndex: number,
    field: keyof FeatureAttributes,
    value: FeatureAttributes[keyof FeatureAttributes]
  ) => void;
  libraries: Array<FeatureLibraryAttributes>;
  onMoveToLibrary: (libraryId: string, featureIndices: Array<number>) => void;
  onDeleteFeatures: (featureIndices: Array<number>) => void;
  isReadOnly: boolean;
  currentLibraryId: string;
}

interface TextCellProps {
  text: string;
  onEdit: ((newText: string) => void) | null;
  elementName: string;
}

const TextCell = ({text, onEdit, elementName}: TextCellProps): JSX.Element => (
  <EditableTextField
    isDisabled={onEdit == null}
    value={text}
    requestChange={onEdit || noop}
    dataTestAttrs={{
      component: 'FeatureLibraryItemRow',
      element: elementName,
      key: text,
    }}
  />
);

type SortableAccessor = 'name' | 'feature_type';

export default function FeatureTable({
  features,
  onUpdateFeature,
  libraries,
  currentLibraryId,
  onMoveToLibrary,
  onDeleteFeatures,
  isReadOnly,
}: FeatureTableProps): JSX.Element {
  const getRowContentHeight = (): number => 50;
  const [selectedFeatureIndices, setSelectedFeatureIndices] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<SortableAccessor | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASCENDING_SORT);

  const columns = [
    {
      header: '',
      accessorKey: 'color',
      flexGrow: 1,
      isCollapsible: false,
      isSortable: false,
      width: 100,
    },
    {
      header: 'Name',
      accessorKey: 'name',
      flexGrow: 5,
      isCollapsible: false,
      isSortable: true,
      sortKey: 'name',
      sortDirection: sortKey === 'name' ? sortDirection : null,
      width: 150,
    },
    {
      header: '',
      accessorKey: 'matchType',
      flexGrow: 4,
      isCollapsible: false,
      isSortable: false,
      width: 100,
    },
    {
      header: 'Type',
      accessorKey: 'featureType',
      flexGrow: 5,
      isCollapsible: false,
      isSortable: true,
      sortKey: 'feature_type',
      sortDirection: sortKey === 'feature_type' ? sortDirection : null,
      width: 150,
    },
    {
      header: 'Bases',
      accessorKey: 'bases',
      flexGrow: 6,
      isCollapsible: false,
      isSortable: false,
      width: 200,
    },
  ];
  return (
    <div>
      <div className={css(styles.actionButtons)}>
        <Dropdown
          menu={
            <Menu width={MenuWidth.SMALL}>
              {libraries.map((library) => (
                <Option
                  key={library.api_identifier}
                  value={library.api_identifier}
                  onSelect={() => {
                    setSelectedFeatureIndices(new Set());
                    onMoveToLibrary(library.api_identifier, Array.from(selectedFeatureIndices));
                  }}
                  isDisabled={library.api_identifier === currentLibraryId}
                  dataTestAttrs={{
                    component: 'FeatureLibraryActionMenu',
                    element: 'targetLibraryOption',
                    key: library.name,
                  }}
                >
                  {library.name}
                </Option>
              ))}
            </Menu>
          }
        >
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            text="Move to"
            isDropdown={true}
            disabled={isReadOnly || selectedFeatureIndices.size === 0}
            dataTestAttrs={{
              component: 'FeatureLibraryActionMenu',
              element: 'moveButton',
            }}
          />
        </Dropdown>
        <Button
          buttonStyle={ButtonStyle.NEGATIVE}
          className={css(styles.deleteButton)}
          size={ButtonSize.SMALL}
          text="Delete"
          onClick={() => {
            setSelectedFeatureIndices(new Set());
            onDeleteFeatures(Array.from(selectedFeatureIndices));
          }}
          disabled={isReadOnly || selectedFeatureIndices.size === 0}
          dataTestAttrs={{
            component: 'FeatureLibraryActionMenu',
            element: 'deleteButton',
          }}
        />
      </div>
      <ScrollableTable
        disableSelection={isReadOnly}
        rowSelectionMode={RowSelectionMode.MULTIPLE}
        onChangeSort={(newSortKey: SortableAccessor) => {
          if (sortKey === newSortKey) {
            setSortDirection(cycleSortDirection(sortDirection));
          } else {
            setSortDirection(SortDirection.ASCENDING_SORT);
            setSortKey(newSortKey);
          }
        }}
        onRowsSelect={(rows, isSelected) => {
          if (isSelected) {
            setSelectedFeatureIndices(
              new Set(
                _.union(
                  Array.from(selectedFeatureIndices),
                  rows.map((row) => row.data.featureIndex)
                )
              )
            );
          } else {
            setSelectedFeatureIndices(
              new Set(
                _.difference(
                  Array.from(selectedFeatureIndices),
                  rows.map((row) => row.data.featureIndex)
                )
              )
            );
          }
        }}
        columns={columns}
        emptyTableText="This feature library does not contain any features yet."
        getRowContentHeight={getRowContentHeight}
        isCompact={true}
        rows={sortedBy(
          features,
          _.compact([
            sortKey && {accessor: sortKey, reversed: sortDirection === SortDirection.DESCENDING_SORT},
          ])
        ).map((feature, featureIndex) => ({
          id: featureIndex.toString(),
          isSelected: selectedFeatureIndices.has(featureIndex),
          data: {
            featureIndex,
            color: (
              <PopoverColorPicker
                className={css(styles.popover)}
                selectedColor={feature.color}
                onSelectColor={(newColor) => {
                  onUpdateFeature(featureIndex, 'color', newColor);
                }}
                floatingInteraction={FloatingInteraction.CLICK}
                asDropdown={false}
                disabled={isReadOnly}
              />
            ),
            name: (
              <TextCell
                text={feature.name}
                onEdit={
                  isReadOnly
                    ? null
                    : (newName) => {
                        onUpdateFeature(featureIndex, 'name', newName);
                      }
                }
                elementName="nameInput"
              />
            ),
            matchType: <TextCell text={feature.match_type} onEdit={null} elementName="matchTypeInput" />,
            featureType: (
              <TextCell
                text={feature.feature_type}
                onEdit={
                  isReadOnly
                    ? null
                    : (newFeatureType) => {
                        onUpdateFeature(featureIndex, 'feature_type', newFeatureType);
                      }
                }
                elementName="featureTypeInput"
              />
            ),
            bases: (
              <TextCell
                text={feature.bases}
                onEdit={
                  isReadOnly
                    ? null
                    : (newBases) => {
                        onUpdateFeature(featureIndex, 'bases', newBases);
                      }
                }
                elementName="residuesInput"
              />
            ),
          },
        }))}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  actionButtons: {
    display: 'flex',
  },
  deleteButton: {marginLeft: '8px'},
  popover: {width: 'fit-content'},
});
